import React, { useLayoutEffect, useMemo, useRef } from 'react'
import cn from 'classnames'
import { CornerDownRight, LockKeyhole, Mail, Trash2 } from 'lucide-react'
import UserIcon from 'components/users/UserIcon'
import useGetUser from 'refactor/hooks/api/useGetUser'
import { getCachedUser } from 'state/cache'
import { directMessageCounterpartId, isDirectMessageConversationHead } from 'lib/directMessages'
import usePanelContext from 'refactor/hooks/usePanelContext'
import useOpenRight from 'refactor/hooks/useOpenRight'
import {
    ancestorEntryNavigationFilter,
    isDeletedAncestor,
    isPrivateAncestor,
    resolveQuestAncestors,
} from 'lib/scrollAncestors'
import type { QuestAncestorEntry } from 'lib/scrollAncestors'
import { userHasFeature } from 'refactor/hooks/useUserHasFeature'
import styles from './quest-ancestor-spine.module.scss'

interface QuestAncestorSpineProps {
    quest?: unknown
}

// The ancestor spine renders the head post of every room above this thread,
// eagerly (text + fixed-size avatars only) so its height is stable from first
// layout. The initial scroll starts past it, making parent context a scroll-up
// instead of a per-level click-climb. This thread's own head post is not a rung
// — it renders in full as the title card directly below.
const QuestAncestorSpine: React.FC<QuestAncestorSpineProps> = ({ quest }) => {
    const { panel, filter, panelContentActive, scrollContainerRef } = usePanelContext()
    const { openRight } = useOpenRight(panel?.panelId)
    const entries = useMemo(() => resolveQuestAncestors(quest), [quest])
    const rootRef = useRef<HTMLDivElement | null>(null)
    const appliedInitialScrollRef = useRef(false)

    useLayoutEffect(() => {
        if (appliedInitialScrollRef.current) return
        if (panelContentActive === false) return
        const spineEl = rootRef.current
        if (!spineEl) return
        const scrollEl =
            scrollContainerRef?.current ||
            (spineEl.closest('.scroll-container') as HTMLElement | null)
        if (!scrollEl || scrollEl.clientHeight <= 0) return
        const spineRect = spineEl.getBoundingClientRect()
        if (spineRect.height <= 0) return
        appliedInitialScrollRef.current = true

        if (scrollEl.scrollTop <= 1) {
            const pendingRestoreTop = panel?.savedScrollTop || 0
            if (pendingRestoreTop > 1) {
                // Unparking panel: display:none zeroed the live scrollTop and
                // PanelContainer's rAF restore will re-apply savedScrollTop,
                // which was captured before the spine existed. Grow the saved
                // offset by our height instead of writing scrollTop ourselves.
                panel.savedScrollTop = pendingRestoreTop + spineRect.height
                return
            }
            // A pending open-at-target placement owns the entry scroll, and its
            // 1px start guard treats any scrollTop write here as a user scroll
            // that cancels placement. The spine is already in layout this
            // commit, so the placement measurement accounts for it unaided.
            if (filter?.answerId || panel?.pendingOpenPlacement) return
            const scrollRect = scrollEl.getBoundingClientRect()
            scrollEl.scrollTop = spineRect.bottom - scrollRect.top + scrollEl.scrollTop
        } else {
            // The anchor owner stores the owned answer id (never 'true') in
            // this dataset key. It compensates above-list growth only under
            // instant_reply_nav, where it observes the whole transform
            // container; in that mode our write would both double-compensate
            // and trip the owner's scroll-mismatch release. Otherwise offset
            // our own height so the viewport content does not shift.
            const anchorOwned = !!scrollEl.dataset.naturalAnswerAnchorOwned
            if (anchorOwned && userHasFeature('instant_reply_nav')) return
            scrollEl.scrollTop += spineRect.height
        }
    })

    if (!entries.length) return null

    // The spine lives in the thread view, where a post opens as the head of its
    // own branch. Keep its answerId in the destination filter so target placement
    // and highlighting own the first paint instead of the generic spine scroll.
    const onClickEntry = (entry: QuestAncestorEntry) => {
        const target = ancestorEntryNavigationFilter(entry)
        if (target) openRight(target)
    }

    return (
        <div className={styles.spineComp} ref={rootRef} data-testid="quest-ancestor-spine">
            {entries.map((entry, ix) => {
                if (isPrivateAncestor(entry)) {
                    const deleted = isDeletedAncestor(entry)
                    return (
                        <div className={styles.lockedEntry} key={`locked-ancestor-${ix}`}>
                            <span className={styles.avatarCol} aria-hidden="true">
                                <span className={styles.lockedBadge}>
                                    {deleted ? <Trash2 size={12} /> : <LockKeyhole size={12} />}
                                </span>
                            </span>
                            <span className={styles.lockedLabel}>
                                {deleted ? 'Deleted post' : 'Locked post'}
                            </span>
                            <span className={styles.rail} aria-hidden="true" />
                        </div>
                    )
                }

                if (isDirectMessageConversationHead(entry, quest as any)) {
                    const next = entries[ix + 1]
                    const leadsOn = next ? next.id : (quest as any)?.parent_id
                    return (
                        <DirectMessageConversationEntry
                            key={entry.id}
                            quest={quest}
                            messageCount={entry.branch_reply_count}
                            onOpen={() => {
                                const questId = entry.branch_quest_id || entry.quest_id
                                if (!questId) return
                                openRight(
                                    leadsOn ? { questId, answerId: String(leadsOn) } : { questId },
                                )
                            }}
                        />
                    )
                }

                const replyCount = entry.branch_reply_count
                return (
                    <button
                        type="button"
                        className={styles.entry}
                        key={entry.id}
                        onClick={() => onClickEntry(entry)}
                        title={entry.content_preview}
                    >
                        <span className={styles.entryMain}>
                            <span className={styles.avatarCol} aria-hidden="true">
                                {entry.user && (
                                    <UserIcon
                                        user={entry.user}
                                        size="small"
                                        contextStyles={styles.entryAvatar}
                                    />
                                )}
                            </span>
                            <span className={styles.entryBody}>
                                {entry.user?.name && (
                                    <span className={styles.entryByline}>{entry.user.name}</span>
                                )}
                                {entry.content_preview && (
                                    <span className={styles.entryText}>
                                        {entry.content_preview}
                                    </span>
                                )}
                            </span>
                        </span>
                        {typeof replyCount === 'number' && (
                            <span className={styles.branchDivider}>
                                <CornerDownRight size={13} />
                                {`${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}
                            </span>
                        )}
                        <span className={styles.rail} aria-hidden="true" />
                    </button>
                )
            })}
        </div>
    )
}

// The top of a direct message's chain is the conversation itself, whose head
// is a system post naming both people. It shows as the two of them instead,
// with the conversation's size, and opens it at the message this branch grew
// from.
function DirectMessageConversationEntry({
    quest,
    messageCount,
    onOpen,
}: {
    quest: unknown
    messageCount?: number
    onOpen: () => void
}) {
    const viewer = gon.currentUser
    const counterpartId = directMessageCounterpartId(quest as any, viewer?.id)
    const { user: fetchedCounterpart } = useGetUser(counterpartId || undefined, {
        enabled: !!counterpartId,
        refetchOnMount: false,
    })
    const counterpart: any = fetchedCounterpart || getCachedUser(counterpartId)
    const name = counterpart?.name || counterpart?.username || 'Them'

    return (
        <button
            type="button"
            className={styles.entry}
            onClick={onOpen}
            title={`Your conversation with ${name}`}
            data-testid="direct-message-conversation-ancestor"
        >
            <span className={styles.entryMain}>
                <span className={styles.avatarCol} aria-hidden="true">
                    <span className={styles.pair}>
                        {counterpartId && (
                            <UserIcon
                                user={counterpart || { id: counterpartId }}
                                size="small"
                                contextStyles={cn(styles.pairAvatar, styles.pairThem)}
                            />
                        )}
                        {viewer && (
                            <UserIcon
                                user={viewer}
                                size="small"
                                contextStyles={cn(styles.pairAvatar, styles.pairYou)}
                            />
                        )}
                    </span>
                </span>
                <span className={styles.entryBody}>
                    <span className={styles.entryByline}>{`${name} and you`}</span>
                </span>
            </span>
            <span className={styles.branchDivider}>
                <Mail size={13} />
                {typeof messageCount === 'number' &&
                    `${messageCount} ${messageCount === 1 ? 'message' : 'messages'} · `}
                <span className={styles.openConversation}>Open conversation</span>
            </span>
            <span className={styles.rail} aria-hidden="true" />
        </button>
    )
}

export default QuestAncestorSpine
